import {Button, DatePicker, Input, Modal, Popover, Radio, Select, Spin, Steps} from 'antd'
import {course_getCourses_Api, course_promotePreview_Api, curriculum_getByOwner_Api} from 'apis'
import {getId, getType} from 'apis/model/base'
import classNames from 'classnames'
import FieldsFactory from 'components/form/FieldsFactory'
import {FormActionGroups} from 'components/form/FormActionBar'
import {createSelectValue, createValue} from 'components/form/utils'
import {Formik, useFormikContext} from 'formik'
import {getDateString, parseDayjs} from 'helpers/datetimeHelpers'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import withTranslate from 'modules/local/withTranslate'
import useAsync from 'modules/asyncCache/useAsync'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import React, {useCallback, useMemo, useState} from 'react'
import {IoCalendarOutline} from 'react-icons/io5'
import {compose, mapProps, nest, pure} from 'recompose'
import {deepTranslate, NullAsDefault, shallowDiff} from 'views/Shared'
import {convertFromString, convertToString} from 'views/SlateEditor/functions'
import {ModalEditorToolbar} from 'views/SlateEditor/SlateEditor'
import {course_status_options} from '..'
import DynamicFieldsDeclaration from '../../../../components/DynamicFields/DynamicFieldDeclaration'
import FormActionBar from '../../../../components/form/FormActionBar'
import SlateEditor from '../../../SlateEditor'
import {MedalSingleSelect} from '../../../Workspace/Medal/MedalSelect'
import {bindQueryParams} from '../../functions/routerHelper'
import {appClassNames} from '../../custom/appClassNames'
import {createControlledFormField, createNumberControlledInput,} from '../../factory/createFormField'
import {notifyOnError} from '../../factory/createErrorEvent'
import OrganizationContainerContext from '../../Organization/OrganizationContainerContext'
import {PiChalkboardSimpleBold} from 'react-icons/pi'
import {GoDotFill} from 'react-icons/go'
import {renderIfElse} from '../../../Shared'
import {LuCornerLeftDown, LuCornerRightDown} from 'react-icons/lu'
import {TbSum, TbTimelineEventText} from 'react-icons/tb'
import TextArea from 'antd/lib/input/TextArea'
import {CustomToggleField} from '../../../../components/form/fields/ToggleField'
import Pure from '../../../../components/Pure'
import {HeaderWithIconLine} from '../../Templates/items/ListHeader'
import Translate from '../../../../modules/local/Translate'
import {ClassroomSvg} from '../../../../components/Svgs/ClassroomSvg'
import {BsShieldFillExclamation} from 'react-icons/bs'
import LookupField from '../../../../components/LookupField'
import {SelectWidget} from '../../custom/ReactSelectComponents'
import dayjs from 'dayjs'
import {ActionModalWrapper} from '../../../../components/form/ActionModalWrapper'
import {ROOT_URL} from '../../../../envs/_current/config'
import TooltipApp from '../../../../components/TooltipApp'
import {
    lengthBetween,
    noDoubleSpaces,
    noLeadingTrailingSpaces,
    noNumber,
    noSpecialChars,
    required,
    runRules,
} from '../../../../components/form/ValidateForm'
import {CategoryField} from '../../../../components/form/fields/CategoryField'
import {privacyOptions} from '../../../../conts/privacyOptions'

// ─── Curriculum selector ──────────────────────────────────────────────────────
// Must be defined before formSchema (const TDZ — used as a component reference).

const CurriculumSelectField = ({name, value, onChange, form}) => {
    const t = useTranslate()
    const owner_id = _.get(form, 'values.owner_id')
    const owner_type = _.get(form, 'values.owner_type')

    const {response, isLoading} = useAsync({
        apiInfo: curriculum_getByOwner_Api,
        query: bindQueryParams([{type: owner_type}, {id: owner_id}]),
    })

    const curricula = _.get(response, 'data.data', [])

    const handleChange = useCallback((newId) => {
        const curriculum = curricula.find((c) => c.id === newId) ?? null
        const hasAbout = !!_.get(form, 'values.about')
        const hasCurriculum = !!_.get(form, 'values.curriculum_id')

        if (newId && hasAbout) {
            Modal.confirm({
                title: t('apply curriculum'),
                content: t('apply curriculum confirm content'),
                okText: t('confirm'),
                cancelText: t('cancel'),
                onOk: () => {
                    form.setFieldValue(name, newId)
                    form.setFieldValue('_curriculum', curriculum)
                    form.setFieldValue('about', undefined)
                },
            })
        } else if (!newId && hasCurriculum) {
            Modal.confirm({
                title: t('confirm'),
                content: t('remove curriculum confirm content'),
                okText: t('confirm'),
                cancelText: t('cancel'),
                onOk: () => {
                    form.setFieldValue(name, null)
                    form.setFieldValue('_curriculum', null)
                },
            })
        } else {
            form.setFieldValue(name, newId ?? null)
            form.setFieldValue('_curriculum', curriculum)
        }
    }, [curricula, form, name, t])

    return (
        <SelectWidget
            loading={isLoading}
            className="w-full"
            placeholder={t('select curriculum')}
            value={value || undefined}
            allowClear
            showSearch
            optionLabelProp="label"
            filterOption={(input, option) =>
                (option?.searchText ?? '').toLowerCase().includes(input.toLowerCase())
            }
            onChange={handleChange}>
            {curricula.map((c) => (
                <Select.Option key={c.id} value={c.id} label={c.title} searchText={c.title}>
                    <div className="flex flex-col gap-0.5 py-0.5">
                        <span className="font-bold text-sm text-gray-700">
                            {c.title}
                        </span>
                        <div className="flex items-center gap-2 tracking-wider">
                            <span className="text-color-200">
                                <GoDotFill size={10} className="mr-1 text-color-500"/>{c.subject_count} {t('subjects')}
                            </span>
                            {c.duration_months > 0 && (
                                <span className="text-color-200">
                                    <GoDotFill size={10} className="mr-1 text-color-500"/>
                                    {c.duration_months} {t('months')}
                                </span>
                            )}
                        </div>
                        {c.description && (
                            <span className="mt-1 text-xs text-color-400 tracking-wider truncate">{c.description}</span>
                        )}
                    </div>
                </Select.Option>
            ))}
        </SelectWidget>
    )
}

export const formSchema = {
    status: {
        title: 'status',
        children: [
            {
                name: 'status',
                component: ({name, value, form, onChange}) => {
                    const options = Array.from(form?.values?.status_options ?? [])
                    return (
                        <Radio.Group
                            name={name}
                            value={value}
                            options={options}
                            onChange={onChange}
                            optionType="button"
                            buttonStyle="solid"
                            className="custom-radio-button"
                        />
                    )
                },
            },
        ],
    },
    academic_year: {
        inline: true,
        className: appClassNames.inline_2,
        children: [
            {
                name: 'academic_year',
                label: 'academic year',
                component: createNumberControlledInput({
                    placeholder: 'academic year',
                    type: 'number',
                    formatter: (value) =>
                        `${value}`.replace(new RegExp(/\B(?=(\d{3})+(?!\d))/g), ''),
                    parser: (value) => {
                        const parsed = value.replace(/\$\s?|(,*)/g, '')
                        return parsed ? Number(parsed) : 0 // ✅ convert sang number
                    },
                }),
            },
        ],
    },
    academic_field: {
        title: 'academic field',
        children: [
            {
                name: 'academic_field',
                component: ({name, value, onChange, form}) => {
                    const values = _.get(form, 'values', {})
                    const owner = _.get(values, 'owner', {})
                    console.log(999998888, values)
                    return (
                        <Translate>
                            {(t) => (
                                <LookupField
                                    createIfNotExists={true}
                                    defaultValue={values?.academic_field}
                                    apiPath={`/lookup/${getType(owner)}/${getId(owner)}/field/academic_field/gets`}
                                    name={name}
                                    placeholder={t('choose or create title')}
                                    handleChange={(e) => {
                                        onChange(createValue('academic_field', e.target.value))
                                    }}
                                    OptionWrapper={({children, ...props}) =>
                                        <div className="lookup-item" {...props}>
                                            {children}
                                        </div>
                                    }
                                />
                            )}
                        </Translate>
                    )
                },
            },
        ],
    },
    title: {
        title: 'title of course',
        children: [
            {
                name: 'title',
                component: createControlledFormField({
                    placeholder: 'title of course',
                    showCount: true,
                    maxLength: 180,
                    className: 'rounded-md',
                }),
            },
        ],
    },
    description: {
        title: 'short description',
        children: [
            {
                name: 'description',
                component: createControlledFormField({
                    rows: 5,
                    showCount: true,
                    maxLength: 500,
                    placeholder: 'short description',
                    Component: TextArea,
                    className: 'scrollBarThin',
                }),
            },
        ],
    },
    idname: {
        title: 'url',
        children: {
            name: 'idname',
            component: compose(
                mapProps(({onChange, value}) => ({
                    onChange,
                    value,
                    name: 'idname',
                    addonBefore: 'https://.../course/',
                    placeholder: 'please input name!',
                    type: 'text',
                    trigger: ['focus'],
                    title: ROOT_URL + '/course/' + value,
                    placement: 'topLeft',
                })),
                pure
            )(nest(TooltipApp, Input)),
        },
    },
    privacy: {
        title: 'privacy',
        children: {
            name: 'privacy',
            component: compose(withTranslate)(({name, translate: t, onChange, value}) => {
                const desc = {
                    1: t('This content is available to everyone.'),
                    2: t('This content is available to members only.'),
                }
                return (
                    <div className="flex flex-col gap-1">
                        <SelectWidget
                            name={name}
                            className="w-full"
                            value={value || undefined}
                            onChange={(v) => onChange(createSelectValue('privacy', v))}
                            placeholder={t('---Choose One---')}
                            options={Object.values(privacyOptions).map(deepTranslate(t))}
                        />
                        {desc[value] && (
                            <p className="text-xs text-color-400 italic leading-relaxed tracking-wide">
                                {desc[value]}
                            </p>
                        )}
                    </div>
                )
            }),
        },
    },
    channel_post_permission: {
        title: 'posting permission',
        children: {
            name: 'channel_post_permission',
            component: compose(
                withTranslate,
                mapProps(({name, value, onChange, translate}) => ({
                    name,
                    value: Number(value) === 3,
                    onChange: (value) => {
                        onChange(createValue(name, Boolean(value) ? 3 : 2))
                    },
                    title: translate('posting permission'),
                    description: translate(
                        'Allow changing posting permissions on the news channel; On: only administrators are allowed to post, Off: all students are allowed to post'
                    ),
                }))
            )(CustomToggleField),
        },
    },
    about: {
        title: 'about course',
        children: [
            {
                name: 'about',
                component: createControlledFormField({
                    Component: SlateEditor,
                    ToolbarComponent: ModalEditorToolbar,
                    className: 'background border rounded-lg',
                    withProps: ({value}) => ({
                        defaultValue: value,
                    }),
                    withValues: ({resetKey}) => ({
                        resetKey,
                    }),
                }),
            },
        ],
    },
    last_enrollment_time: {
        title: 'last enrollment time',
        children: [
            {
                name: 'last_enrollment_time',
                component: compose(
                    withTranslate,
                    mapProps(({name, value, onChange, translate}) => ({
                        name,
                        value,
                        showTime: true,
                        className: 'w-full',
                        format: 'HH:mm - DD/MM/YYYY',
                        suffixIcon: <IoCalendarOutline className="text-color-500"/>,
                        placeholder: translate('ex: 07:00 - 20/10/2010'),
                        // Chỉ cho chọn trong khoảng 100 năm
                        disabledDate: (current) => {
                            const minDate = dayjs().subtract(100, 'year').startOf('day')
                            const maxDate = dayjs().add(100, 'year').endOf('day')
                            return current && (current < minDate || current > maxDate)
                        },
                        onChange: (date, dateString) => {
                            onChange(createValue(name, date ? dayjs(new Date(date)) : null))
                        },
                    }))
                )(DatePicker),
            },
        ],
    },
    started_finished_date: {
        inline: true,
        className: appClassNames.inline_3,
        children: [
            {
                name: 'time_open',
                label: 'start date',
                component: compose(
                    withTranslate,
                    mapProps(({name, value, onChange, translate, form}) => ({
                        name,
                        value,
                        // showTime: true,
                        className: 'w-full',
                        format: 'DD/MM/YYYY',
                        suffixIcon: <IoCalendarOutline className="text-color-500"/>,
                        placeholder: translate('ex: 20/10/2010'),
                        // Chỉ cho chọn trong khoảng 100 năm
                        disabledDate: (current) => {
                            const minDate = dayjs().subtract(100, 'year').startOf('day')
                            const maxDate = dayjs().add(100, 'year').endOf('day')
                            return current && (current < minDate || current > maxDate)
                        },
                        onChange: (date) => {
                            const nextDate = date ? dayjs(date) : null
                            form.setValues({
                                ...form.values,
                                time_open: nextDate,
                                academic_year: nextDate?.year()
                            }, true)
                        }
                    }))
                )(DatePicker),
            },
            {
                name: 'time_close',
                label: 'end date',
                component: compose(
                    withTranslate,
                    mapProps(({name, value, onChange, translate, form}) => {
                        const timeOpen = form?.values?.time_open
                        return {
                            name,
                            value,
                            // showTime: true,
                            disabled: !timeOpen,
                            className: 'w-full',
                            format: 'DD/MM/YYYY',
                            suffixIcon: <IoCalendarOutline className="text-color-500"/>,
                            placeholder: translate('ex: 20/10/2010'),
                            // Chỉ cho chọn trong khoảng 100 năm
                            disabledDate: (current) => {
                                const minDate = dayjs().subtract(100, 'year').startOf('day')
                                const maxDate = dayjs().add(100, 'year').endOf('day')
                                return current && (current < minDate || current > maxDate)
                            },
                            onChange: (date, dateString) => {
                                onChange(createValue(name, date ? dayjs(new Date(date)) : null))
                            },
                        }
                    })
                )(DatePicker),
            },
            {
                name: 'academic_year',
                label: 'academic year',
                component: createNumberControlledInput({
                    placeholder: 'academic year',
                    type: 'number',
                    min: 2000,
                    max: 9999,
                    formatter: (value) =>
                        `${value}`.replace(new RegExp(/\B(?=(\d{3})+(?!\d))/g), ''),
                    parser: (value) => {
                        const parsed = value.replace(/\$\s?|(,*)/g, '')
                        return parsed ? Number(parsed) : undefined
                    },
                    withForm: (form) => ({
                        disabled: !form?.values?.time_open,
                    }),
                }),
            }
        ],
    },

    curriculum_id: {
        title: 'training program',
        children: [
            {name: 'curriculum_id', component: CurriculumSelectField},
        ],
    },

    medal_id: {
        title: 'certification awarded',
        children: [
            {
                name: 'medal_id',
                component: ({name, form, value, onChange}) => {
                    const defaultValue = form?.values?.medal_id
                    return (
                        <Translate>
                            {(t) => (
                                <OrganizationContainerContext.Consumer>
                                    {({organization}) => (
                                        <MedalSingleSelect
                                            {...{
                                                name,
                                                value,
                                                onChange,
                                                defaultValue,
                                            }}
                                            className="custom-select-medal"
                                            placeholder={t('click to select')}
                                            type="medal"
                                            owner_id={getId(organization)}
                                            owner_type={getType(organization)}
                                        />
                                    )}
                                </OrganizationContainerContext.Consumer>
                            )}
                        </Translate>
                    )
                },
            },
        ],
    },

    student_quota: {
        title: 'student quota',
        children: [
            {
                name: 'student_quota',
                component: createNumberControlledInput({
                    placeholder: '0',
                    type: 'number',
                    min: 0,
                    formatter: (value) =>
                        `${value}`.replace(new RegExp(/\B(?=(\d{3})+(?!\d))/g), ','),
                    parser: (value) => {
                        const parsed = value.replace(/[^0-9]/g, '')
                        return parsed ? Number(parsed) : undefined
                    },
                }),
            },
        ],
    },

    metadata: {
        title: 'manage student information by course',
        children: [
            {
                name: 'metadata',
                component: ({name, value, onChange, errors}) => {
                    return (
                        <DynamicFieldsDeclaration
                            name={name}
                            value={value}
                            className="w-full"
                            onChange={(value) => {
                                onChange(createValue(name, value))
                            }}
                            errors={errors?.metadata || {}}
                        />
                    )
                },
            },
        ],
    },

    categories: {
        title: 'categories',
        children: [
            {
                name: 'categories',
                component: CategoryField,
            },
        ],
    },
}

// ─── Curriculum preview (create form) ────────────────────────────────────────
// Shows read-only preview of inherited structure when a curriculum is selected.

const CurriculumPreview = () => {
    const t = useTranslate()
    const {values} = useFormikContext()
    const curriculum = values._curriculum

    if (!curriculum) return null

    const subjects = curriculum.subjects ?? []

    return (
        <div className="shadow-md rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-blue-400 bg-blue-100">
                <span className="font-medium text-xs text-blue-500 uppercase tracking-wide">
                    {t('curriculum')}
                </span>
                <span className="flex-1 font-semibold text-sm text-blue-700 truncate">
                    {curriculum.title}
                </span>
            </div>

            <div className="px-3 py-2 flex items-center gap-2 shrink-0">
                <span className="font-medium text-xs text-color-200">
                    <GoDotFill size={10} className="mr-1 text-color-500"/>
                    {curriculum.subject_count} {t('subjects')}
                </span>
                {curriculum.duration_months > 0 && (
                    <span className="font-medium text-xs text-color-200">
                        <GoDotFill size={10} className="mr-1 text-color-500"/>
                        {curriculum.duration_months} {t('months')}
                    </span>
                )}
            </div>
            {curriculum.description && (
                <p className="px-3 text-xs text-color-300 tracking-wider">
                    {curriculum.description}
                </p>
            )}
            {subjects.length > 0 && (
                <div className="px-3 py-2 flex flex-col gap-1">
                    <span className="font-medium text-xs text-gray-600 uppercase tracking-wide">
                        {t('subjects')}:
                    </span>
                    {subjects.map((s, i) => (
                        <div key={i} className="ml-3 flex items-center gap-2 text-sm">
                            <span className="text-gray-600 w-4 shrink-0">{i + 1}.</span>
                            <span className="flex-1">{s.subject?.title || s.title || '-'}</span>
                            {s.number_of_sessions > 0 && (
                                <span className="text-gray-600 shrink-0">
                                    {s.number_of_sessions} {t('sessions')}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

// ─── Curriculum readonly badge (edit form) ────────────────────────────────────

const CurriculumReadonlyBadge = () => {
    const t = useTranslate()
    const {values} = useFormikContext()

    if (!values.curriculum_id) return null

    const curriculumTitle = values.curriculum?.title

    return (
        <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-color-400 uppercase tracking-wide">
                {t('training program')}
            </span>
            <div
                className="flex items-center gap-2 rounded-md border border-color-50 background-100 px-3 py-2 cursor-not-allowed opacity-70">
                {curriculumTitle ? (
                    <span className="flex-1 text-sm text-color-200">{curriculumTitle}</span>
                ) : (
                    <span className="flex-1 text-sm text-color-400 italic">{t('none')}</span>
                )}
                <span className="text-xs text-color-500 italic shrink-0">{t('not editable')}</span>
            </div>
        </div>
    )
}

const validateTitle = (name, t) => {
    const rules = [
        required(t),
        lengthBetween(2, 180, t),
        noSpecialChars(t),
        noLeadingTrailingSpaces(t),
        noDoubleSpaces(t),
    ].filter(Boolean)
    const message = runRules(name, rules)
    if (message) return {title: message}
}

const validateDescription = (description, t) => {
    const rules = [lengthBetween(1, 500, t)].filter(Boolean)
    const message = runRules(description, rules)
    if (message) return {description: message}
}

const validateAcademicField = (name, t) => {
    const rules = [
        lengthBetween(2, 255, t),
        noNumber(t),
        noSpecialChars(t),
        noLeadingTrailingSpaces(t),
        noDoubleSpaces(t),
    ].filter(Boolean)
    const message = runRules(name, rules)
    if (message) return {academic_field: message}
}

const validateTimeOpen = (timeOpen, typeForm, t) => {
    const rules = [required(t)].filter(Boolean)
    const message = runRules(timeOpen, rules)
    if (message) return {time_open: message}

}

const validateTimeClose = (timeOpen, timeClose, t) => {
    const rules = [required(t)].filter(Boolean)
    const message = runRules(timeClose, rules)
    if (message) return {time_close: message}

    const start = dayjs(timeOpen).startOf('day')
    const end = dayjs(timeClose).startOf('day')
    if (end.isBefore(start)) {
        return {time_close: t('end date must be same as or after start date')}
    }
}

const validateAcademicYear = (value, t) => {
    const rules = [required(t)].filter(Boolean)
    const message = runRules(value, rules)
    if (message) return {academic_year: message}
}

const validateLastEnrollmentTime = (timeOpen, last_enrollment_time, t) => {
    const error = {}
    if (!timeOpen || !last_enrollment_time) return error
    const start = dayjs(timeOpen).startOf('day')
    const enrollmentDeadline = dayjs(last_enrollment_time).startOf('day')
    if (enrollmentDeadline.isAfter(start.add(21, 'day'))) {
        return {
            last_enrollment_time: t('enrollment deadline cannot be more than 3 weeks after the course start date'),
        }
    }

    return error
}

const validate = (t, typeForm) => (values) => {
    const {
        title,
        description,
        academic_field,
        time_open,
        time_close,
        academic_year,
        about,
        last_enrollment_time,
        metadata,
    } = values ?? {}
    const errorTitle = validateTitle(title, t)
    const errorDescription = validateDescription(description, t)
    const errorAcademicField = validateAcademicField(academic_field, t)
    const errorTimeOpen = validateTimeOpen(time_open, typeForm, t)
    const errorTimeClose = validateTimeClose(time_open, time_close, t)
    const errorAcademicYear = validateAcademicYear(academic_year, t)
    const errorLastEnrollmentTime = validateLastEnrollmentTime(
        time_open,
        last_enrollment_time,
        t
    )

    // ── validate metadata ──────────────────────────────────────────
    const metaItems = Array.isArray(metadata) ? metadata : []

    if (metaItems.length === 0) {
        // bỏ dòng này nếu metadata không bắt buộc phải có ít nhất 1 field
        // errors.metadata = t('required field')
    }

    const metaErrors = {}

    metaItems.forEach(function (item, index) {
        // title bắt buộc
        if (!item.title || !item.title.trim()) {
            metaErrors['[' + index + '].title'] = t('required field')
        } else if (item.title.trim().length > 120) {
            metaErrors['[' + index + '].title'] = t('title_max_120')
        }

        // description max 500
        if (item.description && item.description.trim().length > 500) {
            metaErrors['[' + index + '].description'] = t('description_max_500')
        }

        // options bắt buộc nếu là radioField / checkboxField
        if (['radioField', 'checkboxField'].includes(item.ctrl_type)) {
            var opts = Array.isArray(item.options) ? item.options : []
            if (opts.length === 0) {
                metaErrors['[' + index + '].options'] = t('options_min_1')
            } else {
                opts.forEach(function (opt, ii) {
                    if (!opt || !String(opt).trim()) {
                        metaErrors['[' + index + '].options[' + ii + ']'] =
                            t('required field')
                    } else if (String(opt).trim().length > 120) {
                        metaErrors['[' + index + '].options[' + ii + ']'] =
                            t('option_max_120')
                    }
                })
            }
        }
    })

    return {
        ...errorTitle,
        ...errorDescription,
        ...errorAcademicField,
        ...errorTimeOpen,
        ...errorTimeClose,
        ...errorAcademicYear,
        ...errorLastEnrollmentTime,
        // ...(Object.keys(metaErrors).length > 0
        //   ? { metadata: t('invalid data') }
        //   : {}),
    }
}

// Wraps create form body — reads curriculum_id to conditionally hide/show fields.
const CreateCourseFormContent = () => {
    const t = useTranslate()
    const {values} = useFormikContext()
    const hasCurriculum = !!values.curriculum_id

    return (
        <div className="flex flex-col gap-3">
            <Pure>
                <FieldsFactory formSchema={[formSchema.curriculum_id]}/>
            </Pure>

            {hasCurriculum && <CurriculumPreview/>}

            <Pure>
                <FieldsFactory
                    formSchema={[
                        formSchema.title,
                        formSchema.description,
                        formSchema.academic_field,
                        formSchema.started_finished_date,
                    ]}
                />
            </Pure>

            {!hasCurriculum && (
                <Pure>
                    <FieldsFactory formSchema={[formSchema.about]}/>
                </Pure>
            )}

            <div className="flex flex-col gap-3">
                <HeaderWithIconLine
                    className="pt-3"
                    renderTitle={() => (
                        <div className="flex items-center gap-2">
                            <GoDotFill size={16} className="text-color-500"/>
                            <span className="font-semibold text-base text-color-400 uppercase tracking-wider">
                                {t('registered information')}
                            </span>
                        </div>
                    )}
                />
                <Pure>
                    <FieldsFactory formSchema={[formSchema.last_enrollment_time, formSchema.student_quota]}/>
                </Pure>
            </div>
        </div>
    )
}

export const CreateCourseModal = ({
                                      owner_id,
                                      owner_type,
                                      onCancel = NullAsDefault,
                                      onSubmit = NullAsDefault,
                                  }) => {
    const t = useTranslate()

    const initialValues = useMemo(() => ({
        owner_id,
        owner_type,
        title: undefined,
        description: undefined,
        curriculum_id: undefined,
        _curriculum: undefined,
    }), [owner_id, owner_type])

    return (
        <Formik
            validate={validate(t, "Create")}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={initialValues}
            onSubmit={(values) => {
                const {
                    about,
                    resetKey,
                    owner_id,
                    owner_type,
                    time_open,
                    time_close,
                    last_enrollment_time,
                    _curriculum,
                    ...rest
                } = values ?? {}
                const hasCurriculum = !!rest.curriculum_id
                onSubmit(
                    _.omitBy(
                        {
                            ...rest,
                            time_open: getDateString(time_open),
                            time_close: getDateString(time_close),
                            // about will be snapshotted from curriculum on backend when curriculum_id is set
                            about: (!hasCurriculum && about) ? convertToString(about) : undefined,
                            last_enrollment_time: getDateString(last_enrollment_time),
                        },
                        _.isUndefined
                    )
                )
            }}>
            <ActionModalWrapper action={<FormActionGroups submitProps={{label: 'create'}}/>}>
                <CreateCourseFormContent/>
            </ActionModalWrapper>
        </Formik>
    )
}

export const EditCourseModal = ({
                                    owner_id,
                                    owner_type,
                                    onCancel = NullAsDefault,
                                    onSubmit = NullAsDefault,
                                    initialValues = {},
                                }) => {
    const t = useTranslate()
    const [current, setCurrent] = useState('overview')

    const _initialValues = useMemo(() => {
        const {
            about,
            time_open,
            time_close,
            last_enrollment_time,
            student_quota,
            metadata = [],
            categories,
            ...rest
        } = initialValues ?? {}
        const status_options = [
            course_status_options.normal,
            course_status_options.closed,
        ].map(deepTranslate(t))
        return {
            ...rest,
            owner_id,
            owner_type,
            status_options,
            medal_id: rest?.medal?.id,
            curriculum_id: rest?.curriculum_id ?? null,
            about: convertFromString(about),
            time_open: parseDayjs(time_open),
            time_close: parseDayjs(time_close),
            last_enrollment_time: parseDayjs(last_enrollment_time),
            student_quota: student_quota ? Number(student_quota) : undefined,
            metadata: metadata ?? [],
            categories: _.isArray(categories)
                ? categories.map((category) => category?.id)
                : [],
        }
    }, [initialValues, owner_id, owner_type])

    return (
        <Formik
            validate={validate(t, "Edit")}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={_initialValues}
            onSubmit={(values) => {
                const changes = shallowDiff(values, _initialValues)
                const {
                    about,
                    resetKey,
                    owner_id,
                    owner_type,
                    time_open,
                    time_close,
                    status_options,
                    last_enrollment_time,
                    metadata,
                    categories,
                    ...rest
                } = changes ?? {}
                onSubmit(
                    _.omitBy(
                        {
                            ...rest,
                            metadata: JSON.stringify(metadata),
                            time_open: getDateString(time_open),
                            time_close: getDateString(time_close),
                            about: about ? convertToString(about) : undefined,
                            last_enrollment_time: getDateString(last_enrollment_time),
                            categories: categories
                                ? Array.from(categories).join(',')
                                : undefined,
                        },
                        _.isUndefined
                    )
                )
            }}>
            <React.Fragment>
                {/*<ErrorSummaries />*/}
                <ActionModalWrapper
                    tab={current}
                    action={<FormActionBar submitText="save"/>}
                    onTabChange={(key) => {
                        setCurrent(key)
                    }}
                    tabs={[
                        {
                            key: 'overview',
                            label: 'overview',
                            component: ({child}) => child,
                        },
                        {
                            key: 'metadata',
                            label: 'manage student information by course',
                            component: () => (
                                <FieldsFactory formSchema={[formSchema.metadata]}/>
                            ),
                        },
                        {
                            key: 'about',
                            label: 'about course',
                            component: () => (
                                <FieldsFactory formSchema={[formSchema.about]}/>
                            ),
                        },
                        {
                            key: 'medal',
                            label: 'certification',
                            component: () => (
                                <div className="flex flex-col gap-3">
                                    <FieldsFactory formSchema={[formSchema.medal_id]}/>
                                    <div className="font-medium text-color-100 italic">
                                        {`* ${t('explain certification')}`}
                                    </div>
                                    <div className="space-x-1 text-sm italic">
                    <span className="font-medium text-color-100">
                      {`* ${t('note')} : `}
                    </span>
                                        <span className="text-color-400">
                      {t('note explain certification')}
                    </span>
                                    </div>
                                </div>
                            ),
                        },
                    ]}>
                    <CurriculumReadonlyBadge/>

                    <Pure>
                        <div className="flex flex-col gap-3">
                            <HeaderWithIconLine
                                className="pt-2"
                                renderTitle={() => (
                                    <div className="flex items-center gap-2">
                                        <GoDotFill size={16} className="text-color-500"/>
                                        <span
                                            className="font-semibold text-base text-color-400 uppercase tracking-wider">
                      {t('basic information')}
                    </span>
                                    </div>
                                )}
                            />
                            <div className="ml-2 flex flex-col gap-3">
                                <FieldsFactory
                                    formSchema={[
                                        formSchema.status,
                                        formSchema.title,
                                        formSchema.idname,
                                        formSchema.description,
                                        formSchema.categories,
                                        formSchema.academic_field,
                                    ]}
                                />
                            </div>
                        </div>
                    </Pure>

                    <Pure>
                        <div className="flex flex-col gap-3">
                            <HeaderWithIconLine
                                className="pt-2 mt-3"
                                renderTitle={() => (
                                    <div className="flex items-center gap-2">
                                        <GoDotFill size={16} className="text-color-500"/>
                                        <span
                                            className="font-semibold text-base text-color-400 uppercase tracking-wider">
                      {t('schedule')}
                    </span>
                                    </div>
                                )}
                            />
                            <div className="ml-2 flex flex-col gap-3">
                                <FieldsFactory formSchema={[formSchema.started_finished_date]}/>
                            </div>
                        </div>
                    </Pure>

                    <Pure>
                        <div className="flex flex-col gap-3">
                            <HeaderWithIconLine
                                className="pt-2 mt-3"
                                renderTitle={() => (
                                    <div className="flex items-center gap-2">
                                        <GoDotFill size={16} className="text-color-500"/>
                                        <span
                                            className="font-semibold text-base text-color-400 uppercase tracking-wider">
                      {t('registered information')}
                    </span>
                                    </div>
                                )}
                            />
                            <div className="ml-2 flex flex-col gap-3">
                                <FieldsFactory formSchema={[formSchema.last_enrollment_time, formSchema.student_quota]}/>
                            </div>
                        </div>
                    </Pure>

                    <Pure>
                        <div className="flex flex-col gap-3">
                            <HeaderWithIconLine
                                className="pt-2 mt-3"
                                renderTitle={() => (
                                    <div className="flex items-center gap-2">
                                        <BsShieldFillExclamation
                                            size={16}
                                            className="text-color-500"
                                        />
                                        <span
                                            className="font-semibold text-base text-color-400 uppercase tracking-wider">
                      {t('posting permission')}
                    </span>
                                    </div>
                                )}
                            />
                            <div className="ml-2 flex flex-col gap-3">
                                <FieldsFactory formSchema={[formSchema.channel_post_permission]}/>
                            </div>
                        </div>
                    </Pure>

                    <Pure>
                        <div className="flex flex-col gap-3">
                            <HeaderWithIconLine
                                className="pt-2 mt-3"
                                renderTitle={() => (
                                    <div className="flex items-center gap-2">
                                        <BsShieldFillExclamation
                                            size={16}
                                            className="text-color-500"
                                        />
                                        <span
                                            className="font-semibold text-base text-color-400 uppercase tracking-wider">
                      {t('privacy')}
                    </span>
                                    </div>
                                )}
                            />
                            <div className="ml-2 flex flex-col gap-3">
                                <FieldsFactory formSchema={[formSchema.privacy]}/>
                            </div>
                        </div>
                    </Pure>
                </ActionModalWrapper>
            </React.Fragment>
        </Formik>
    )
}

export const CopyCourseModal = ({
                                    owner_id,
                                    owner_type,
                                    onCancel = NullAsDefault,
                                    onSubmit = NullAsDefault,
                                    initialValues = {},
                                }) => {
    const t = useTranslate()

    const _initialValues = useMemo(() => ({
        title: _.get(initialValues, 'title'),
        time_open: parseDayjs(_.get(initialValues, 'time_open')),
        time_close: parseDayjs(_.get(initialValues, 'time_close')),
        academic_year: _.get(initialValues, 'academic_year'),
    }), [initialValues])

    return (
        <Formik
            validate={validate(t)}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={_initialValues}
            onSubmit={(values) => {
                const {time_open, time_close, ...rest} = values
                onSubmit({
                    ...rest,
                    time_open: getDateString(time_open),
                    time_close: getDateString(time_close),
                })
            }}>
            <React.Fragment>
                <div className="flex flex-col gap-4">
                    <FieldsFactory formSchema={[formSchema.title]}/>
                    <FieldsFactory formSchema={[formSchema.started_finished_date]}/>
                </div>
                <FormActionBar submitText="duplicate"/>
            </React.Fragment>
        </Formik>
    )
}

export const PromoteStudentsWizard = ({
                                          owner_id,
                                          owner_type,
                                          course_id,
                                          isSubmitting = false,
                                          onCancel = NullAsDefault,
                                          onSubmit = NullAsDefault,
                                      }) => {
    const t = useTranslate()
    const [step, setStep] = useState(0)
    const [sourceCourseId, setSourceCourseId] = useState(null)
    const [classMappings, setClassMappings] = useState({})
    const [previewData, setPreviewData] = useState(null)
    const [isFetching, setIsFetching] = useState(false)

    const {response: coursesResponse, isLoading: coursesLoading} = useAsync({
        apiInfo: course_getCourses_Api,
        query: bindQueryParams([{type: owner_type}, {id: owner_id}]),
    })
    const courses = _.get(coursesResponse, 'data.data', []).filter((c) => c.id !== course_id)

    const {handleAsyncAction: fetchPreview} = useAsyncAction({
        apiInfo: course_promotePreview_Api,
        onSuccess: (result) => {
            setPreviewData(result)
            setIsFetching(false)
        },
        onError: (error, errorObj) => {
            setIsFetching(false)
            notifyOnError(t)(error, errorObj)
        },
    })

    const sourceClasses = _.get(previewData, 'source_classes', [])
    const targetClasses = _.get(previewData, 'target_classes', [])

    const usedTargetIds = useMemo(
        () => Object.values(classMappings).filter(Boolean),
        [classMappings]
    )

    const mappedPairs = useMemo(
        () =>
            Object.entries(classMappings)
                .filter(([, tid]) => Boolean(tid))
                .map(([sid, tid]) => ({
                    sourceClass: sourceClasses.find((c) => c.id === sid),
                    targetClass: targetClasses.find((c) => c.id === tid),
                }))
                .filter(({sourceClass, targetClass}) => sourceClass && targetClass),
        [classMappings, sourceClasses, targetClasses]
    )

    const totalCanPromote = mappedPairs.reduce((s, {sourceClass}) => s + (sourceClass?.can_promote ?? 0), 0)
    const totalSkip = mappedPairs.reduce(
        (s, {sourceClass}) => s + (sourceClass?.already_in_target ?? 0) + (sourceClass?.not_completed ?? 0),
        0
    )

    const autoMap = useCallback(() => {
        const newMappings = {}
        sourceClasses.forEach((sc) => {
            const match = targetClasses.find(
                (tc) => tc.title.toLowerCase().trim() === sc.title.toLowerCase().trim()
            )
            newMappings[sc.id] = match ? match.id : null
        })
        setClassMappings(newMappings)
    }, [sourceClasses, targetClasses])

    const setMapping = useCallback((sourceId, targetId) => {
        setClassMappings((prev) => ({...prev, [sourceId]: targetId || null}))
    }, [])

    const goToStep1 = () => {
        setPreviewData(null)
        setClassMappings({})
        setIsFetching(true)
        fetchPreview({source_course_id: sourceCourseId}, {id: course_id})
        setStep(1)
    }

    const handleConfirm = () => {
        const class_mappings = JSON.stringify(
            mappedPairs.map(({sourceClass, targetClass}) => ({
                source_class_id: sourceClass.id,
                target_class_id: targetClass.id,
            }))
        )
        onSubmit({source_course_id: sourceCourseId, class_mappings})
    }

    const STEPS = [
        {title: t('source course')},
        {title: t('map classes')},
        {title: t('confirm')},
    ]

    return (
        <Spin spinning={isSubmitting} tip={t('processing')}>
            <div className="flex flex-col space-y-10">
                <Steps current={step} items={STEPS} size="small"/>

                {/* Bước 0: Chọn khóa nguồn */}
                {step === 0 && (
                    <div className="flex flex-col gap-4">
                        <SelectWidget
                            loading={coursesLoading}
                            className="w-full"
                            placeholder={t('select source course')}
                            value={sourceCourseId || undefined}
                            allowClear
                            showSearch
                            optionLabelProp="label"
                            filterOption={(input, option) =>
                                (option?.searchText ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            onChange={(value) => {
                                setSourceCourseId(value || null)
                                setClassMappings({})
                                setPreviewData(null)
                            }}>
                            {courses.map((c) => (
                                <Select.Option key={c.id} value={c.id} label={c.title} searchText={c.title}>
                                    <div className="flex flex-col gap-0.5 py-0.5">
                                        <span className="font-semibold text-sm">{c.title}</span>
                                        {c.academic_year > 0 && (
                                            <span className="text-xs text-color-400">{c.academic_year}</span>
                                        )}
                                    </div>
                                </Select.Option>
                            ))}
                        </SelectWidget>
                        <div className="flex justify-end">
                            <Button type="primary" disabled={!sourceCourseId} onClick={goToStep1}>
                                {t('next')}
                            </Button>
                        </div>
                    </div>
                )}

                {/* Bước 1: Ghép lớp */}
                {step === 1 && (
                    <div className="flex flex-col gap-3">
                        {isFetching ? (
                            <div className="flex justify-center py-10">
                                <Spin size="default"/>
                            </div>
                        ) : sourceClasses.length === 0 ? (
                            <div className="text-center text-sm text-color-400 py-8">
                                {t('no classes in source course')}
                            </div>
                        ) : (
                            <>
                                <div className="flex items-center justify-between">
                                <span className="text-xs text-color-400">
                                    {sourceClasses.length} {t('classes')} → {targetClasses.length} {t('classes')}
                                </span>
                                    <Button size="small" onClick={autoMap}>
                                        {t('auto map')}
                                    </Button>
                                </div>
                                <div className="rounded-lg border border-color-50 overflow-hidden">
                                    <div
                                        className="grid items-center px-3 py-2 background-200 text-xs font-semibold text-color-300 uppercase tracking-wide"
                                        style={{gridTemplateColumns: '1fr 60px 20px 1fr'}}>
                                        <span>{t('source class')}</span>
                                        <span className="text-right">{t('students')}</span>
                                        <span/>
                                        <span className="pl-2">{t('target class')}</span>
                                    </div>
                                    {sourceClasses.map((sc, index) => (
                                        <div
                                            key={sc.id}
                                            className={classNames(
                                                'grid items-center px-3 py-2 text-sm border-t border-color-50',
                                                index % 2 === 0 ? 'background' : 'background-50'
                                            )}
                                            style={{gridTemplateColumns: '1fr 60px 20px 1fr'}}>
                                            <div className="flex flex-col min-w-0 pr-2">
                                                <span className="font-medium truncate">{sc.title}</span>
                                                {sc.already_in_target > 0 && (
                                                    <span className="text-xs text-color-400">
                                                    {sc.already_in_target} {t('already in course')}
                                                </span>
                                                )}
                                            </div>
                                            <span className="text-right text-xs font-mono text-color-300">
                                            {sc.student_count}
                                        </span>
                                            <span className="text-center text-color-400 text-xs">→</span>
                                            <SelectWidget
                                                size="small"
                                                className="w-full"
                                                placeholder={t('skip')}
                                                value={classMappings[sc.id] || undefined}
                                                allowClear
                                                onChange={(val) => setMapping(sc.id, val)}>
                                                {targetClasses.map((tc) => (
                                                    <Select.Option
                                                        key={tc.id}
                                                        value={tc.id}
                                                        disabled={
                                                            usedTargetIds.includes(tc.id) &&
                                                            classMappings[sc.id] !== tc.id
                                                        }>
                                                        <span>{tc.title}</span>
                                                        {tc.student_count > 0 && (
                                                            <span className="ml-1 text-xs text-color-400">
                                                            ({tc.student_count})
                                                        </span>
                                                        )}
                                                    </Select.Option>
                                                ))}
                                            </SelectWidget>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                        <div className="flex justify-between pt-1">
                            <Button
                                onClick={() => {
                                    setStep(0)
                                    setPreviewData(null)
                                    setClassMappings({})
                                }}>
                                {t('back')}
                            </Button>
                            <Button
                                type="primary"
                                disabled={mappedPairs.length === 0}
                                onClick={() => setStep(2)}>
                                {t('next')}
                            </Button>
                        </div>
                    </div>
                )}

                {/* Bước 2: Xác nhận */}
                {step === 2 && (
                    <div className="flex flex-col gap-4">
                        <div className="rounded-lg border border-color-50 overflow-hidden">
                            <div
                                className="grid items-center px-3 py-2 background-200 text-xs font-semibold text-color-300 uppercase tracking-wide"
                                style={{gridTemplateColumns: '1fr 20px 1fr 70px 70px'}}>
                                <span>{t('source class')}</span>
                                <span/>
                                <span>{t('target class')}</span>
                                <span className="text-right text-green-600">{t('can promote')}</span>
                                <span className="text-right">{t('skip')}</span>
                            </div>
                            {mappedPairs.map(({sourceClass, targetClass}, index) => (
                                <div
                                    key={sourceClass.id}
                                    className={classNames(
                                        'grid items-center px-3 py-2 text-sm border-t border-color-50',
                                        index % 2 === 0 ? 'background' : 'background-50'
                                    )}
                                    style={{gridTemplateColumns: '1fr 20px 1fr 70px 70px'}}>
                                    <span className="font-medium truncate pr-2">{sourceClass.title}</span>
                                    <span className="text-center text-color-400 text-xs">→</span>
                                    <span className="truncate pr-2">{targetClass.title}</span>
                                    <span className="text-right font-semibold text-green-600">
                                    {sourceClass.can_promote}
                                </span>
                                    <span className="text-right text-color-400 text-xs">
                                    {(sourceClass.already_in_target ?? 0) + (sourceClass.not_completed ?? 0)}
                                </span>
                                </div>
                            ))}
                            <div
                                className="grid items-center px-3 py-2 border-t border-color-200 background-100 text-sm font-semibold"
                                style={{gridTemplateColumns: '1fr 20px 1fr 70px 70px'}}>
                                <span className="uppercase text-xs text-color-300 tracking-wide">{t('total')}</span>
                                <span/>
                                <span/>
                                <span className="text-right text-green-600">{totalCanPromote}</span>
                                <span className="text-right text-color-400 text-xs">{totalSkip}</span>
                            </div>
                        </div>

                        {totalCanPromote > 0 && (
                            <div
                                className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
                                {totalCanPromote} {t('students will be promoted')}
                            </div>
                        )}

                        {totalCanPromote === 0 && (
                            <div
                                className="rounded-lg bg-yellow-50 border border-yellow-200 px-4 py-3 text-sm text-yellow-700">
                                {t('no students eligible for transfer')}
                            </div>
                        )}

                        <div className="flex justify-between">
                            <Button onClick={() => setStep(1)} disabled={isSubmitting}>{t('back')}</Button>
                            <Button
                                type="primary"
                                disabled={totalCanPromote === 0 || isSubmitting}
                                loading={isSubmitting}
                                onClick={handleConfirm}>
                                {t('promote students')}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </Spin>
    )
}

export const TutorialCourseModalContent = ({onCancel, onHandleCreate}) => {
    const t = useTranslate()

    const ContentBlock = ({
                              right = false,
                              style,
                              icon,
                              title,
                              content,
                              renderContent,
                              className,
                          }) => (
        <div
            style={style}
            className={classNames(
                'cursor-pointer hover:shadow-items-primary border border-primary-200 hover:border-primary-600 relative',
                className
            )}>
            <div className="flex flex-col">
                <div
                    style={{
                        borderTopLeftRadius: right === true ? 5 : 20,
                        borderTopRightRadius: 20,
                    }}
                    className="flex items-center gap-2 px-3 py-2 bg-primary-50 leading-tight">
                    {icon}
                    <span className="font-bold text-primary uppercase tracking-wider">
            {t(title)}
          </span>
                </div>
                {renderIfElse(
                    renderContent,
                    renderContent && renderContent(),
                    <div className="px-3 py-2 background rounded-b-lg text-xs text-color-300 italic">
                        {t(content)}
                    </div>
                )}
                <div className="text-right mx-3 mb-1 text-2xs text-color-500 hover:text-color-100 italic">
                    {`${t('more')}  >>`}
                </div>
            </div>
        </div>
    )

    const PopupBlock = ({content, subTitle, example, className}) => (
        <div className={classNames('flex flex-col gap-1', className)}>
            <div className="text-xs text-color-200 italic">{t(content)}</div>
            <div className="text-sm text-color-500 italic leading-tight">
                {t(subTitle)}
            </div>
            <div className="ml-1">{example}</div>
        </div>
    )

    const ItemExample = ({title}) => (
        <span className="space-x-1">
      <GoDotFill
          size={10}
          className="text-color-500"
      />
      <span className="text-xs italic">{title}</span>
    </span>
    )

    const CustomPopover = ({popupContent, content}) => (
        <Popover
            classNames={{root: 'PopupContent'}}
            placement="left"
            mouseEnterDelay={0.3}
            destroyOnHidden
            trigger="click"
            content={popupContent}>
            {content}
        </Popover>
    )

    return (
        <div className="flex flex-col gap-4">
            {/*<ListHeaderWithLine title="Course Management Guide" line={false} className="text-xl"/>*/}
            <div className="grid items-center grid-cols-2 gap-6">
                <CustomPopover
                    popupContent={
                        <PopupBlock
                            className="p-1"
                            content="course tutorial extra description"
                            subTitle="example"
                            example={
                                <div className="flex flex-col gap-1">
                                    <ItemExample title="Khóa Huấn luyện Huynh trưởng cấp I - 2024"/>
                                    {/*<ItemExample title="Khóa Huấn luyện Huynh trưởng cấp II - 2024" />*/}
                                </div>
                            }
                        />
                    }
                    content={
                        <div>
                            <ContentBlock
                                style={{borderRadius: '20px 20px 5px 20px'}}
                                icon={
                                    <PiChalkboardSimpleBold
                                        size={14}
                                        className="text-primary"
                                    />
                                }
                                title="course"
                                content="course tutorial description"
                                renderContent={() => (
                                    <div
                                        className="flex flex-col space-y-2 px-3 py-2 background rounded-b-lg text-xs text-color-300 italic">
                                        <div className="text-color text-sm font-medium leading-tight">
                                            {t(
                                                'A Course is a feature that provides information about content, objectives, and training opportunities, managing basic teaching content.'
                                            )}
                                        </div>
                                        <div>
                                            {t(
                                                'The goal of creating a Course is to publish the teaching program (content, timing) and outcomes after the training process for students.'
                                            )}
                                        </div>
                                        <div>
                                            {t(
                                                "It allows learners to view information and directly register for the course through the 'Course Registration' function."
                                            )}
                                        </div>
                                    </div>
                                )}
                            />
                        </div>
                    }
                />
                <div className="flex flex-center">
                    <LuCornerRightDown
                        size={60}
                        style={{color: '#e7e7e7'}}
                    />
                </div>
            </div>
            <div className="grid items-center grid-cols-2 gap-6">
                <div className="flex flex-center">
                    <LuCornerLeftDown
                        size={60}
                        style={{color: '#e7e7e7'}}
                    />
                </div>
                <CustomPopover
                    popupContent={
                        <PopupBlock
                            content="classes tutorial extra description"
                            subTitle="example"
                            example={
                                <div className="flex flex-col gap-1">
                                    <ItemExample title="Lớp 1 - 1"/>
                                    <ItemExample title="Lớp giáo lý dự tòng 1 ( buổi tối 2 4 6 )"/>
                                </div>
                            }
                        />
                    }
                    content={
                        <div>
                            <ContentBlock
                                right={true}
                                style={{borderRadius: '5px 20px 20px 20px'}}
                                icon={
                                    <ClassroomSvg
                                        size={13}
                                        className="text-primary"
                                    />
                                }
                                title="classes"
                                content="classes tutorial description"
                                renderContent={() => (
                                    <div
                                        className="flex flex-col space-y-2 px-3 py-2 background rounded-b-lg text-xs text-color-300 italic">
                                        <div className="text-color text-sm font-medium leading-tight">
                                            {t(
                                                'Classes under the Course plan effective teaching through organized classes.'
                                            )}
                                        </div>
                                        <div>
                                            {t(
                                                'A class is an administrative unit created to ensure effective organization of teaching.'
                                            )}
                                        </div>
                                        <div>
                                            {t(
                                                'Add classes in the management section for each course. Class division is based on the need for organizing teaching, usually depending on the number of students, and timing according to groups of students.'
                                            )}
                                        </div>
                                    </div>
                                )}
                            />
                        </div>
                    }
                />
            </div>
            <div className="grid items-center grid-cols-2 gap-6">
                <CustomPopover
                    popupContent={
                        <div className="flex flex-col">
                            <PopupBlock
                                content="attendance tutorial extra description"
                                subTitle="example"
                                example={
                                    <div className="flex flex-col">
                                        <ItemExample title="Bảng điểm danh 1"/>
                                        <ItemExample title="Bảng điểm danh 2"/>
                                    </div>
                                }
                            />
                            <div className="border-b my-1"/>
                            <PopupBlock
                                content="grade tutorial extra description"
                                subTitle="example"
                                example={
                                    <div className="flex flex-col">
                                        <ItemExample title="Bảng điểm 1"/>
                                        <ItemExample title="Bảng điểm 2"/>
                                    </div>
                                }
                            />
                        </div>
                    }
                    content={
                        <div>
                            <ContentBlock
                                style={{borderRadius: '20px 20px 5px 20px'}}
                                icon={
                                    <TbTimelineEventText
                                        size={13}
                                        className="text-primary"
                                    />
                                }
                                title="activities during the training process"
                                renderContent={() => (
                                    <div className="flex flex-col gap-6 flex-1 px-3 py-2 my-4">
                                        <div className="text-color text-sm font-medium leading-tight">
                                            {t(
                                                "Monitoring the training process: attendance and students' score results."
                                            )}
                                        </div>
                                        <div className="flex flex-col">
                      <span className="font-bold text-sm text-color-000 uppercase">
                        <GoDotFill
                            size={10}
                            className="text-color-000 mr-1"
                        />
                          {`${t('attendances')} : `}
                      </span>
                                            <span className="ml-2 text-xs text-color-300 italic">
                        {t(
                            'Record participation through the attendance function in the class management section.'
                        )}
                      </span>
                                        </div>
                                        <div className="flex flex-col">
                      <span className="font-bold text-sm text-color-000 uppercase">
                        <GoDotFill
                            size={10}
                            className="text-color-000 mr-1"
                        />
                          {`${t('grades')} : `}
                      </span>
                                            <span className="ml-2 text-xs text-color-300 italic">
                        {t(
                            'Record training results through the results of exams organized during the course.'
                        )}
                      </span>
                                        </div>
                                    </div>
                                )}
                            />
                        </div>
                    }
                />
                <div className="flex flex-center">
                    <LuCornerRightDown
                        size={60}
                        style={{color: '#e7e7e7'}}
                    />
                </div>
            </div>
            <div className="grid items-center grid-cols-2 gap-6">
                <div className="flex flex-center">
                    <LuCornerLeftDown
                        size={60}
                        style={{color: '#e7e7e7'}}
                    />
                </div>
                <CustomPopover
                    popupContent={
                        <PopupBlock
                            className="p-1"
                            content="score summary tutorial extra description"
                            subTitle="example"
                            example={
                                <div className="flex flex-col gap-1">
                                    <ItemExample title="Lớp học -> Học viên -> Đánh giá cuối khóa"/>
                                    <ItemExample
                                        title="Workspace -> Khóa học -> QL thông tin dưới khóa -> Lớp học -> Học viên -> Đánh giá cuối khóa"/>
                                </div>
                            }
                        />
                    }
                    content={
                        <div>
                            <ContentBlock
                                right={true}
                                style={{borderRadius: '5px 20px 20px 20px'}}
                                icon={
                                    <TbSum
                                        size={13}
                                        className="text-primary"
                                    />
                                }
                                title="End-of-Course Evaluation"
                                content="score summary tutorial description"
                                renderContent={() => (
                                    <div
                                        className="flex flex-col space-y-2 px-3 py-2 background rounded-b-lg text-xs text-color-300 italic">
                                        <div className="text-color text-sm font-medium leading-tight">
                                            {t('Synthesize final results and assess attendance.')}
                                        </div>
                                        <div>
                                            {t(
                                                'Access the class management section, evaluate the end of the course for each student to view and record results (including attendance assessment and exam results, helping to comprehensively assess performance).'
                                            )}
                                        </div>
                                    </div>
                                )}
                            />
                        </div>
                    }
                />
            </div>

            <div className="grid items-center grid-cols-2 gap-6">
                <div className="flex flex-col flex-center gap-2 py-10">
                    <Button
                        type="primary"
                        className="rounded-lg no-border"
                        onClick={() => onHandleCreate && onHandleCreate()}>
                        {t('create course')}
                    </Button>
                    <span className="text-sm text-color-500 italic">
            {`* ${t('click here to create a new course')}`}
          </span>
                </div>
            </div>
        </div>
    )
}
